/**
 * Carte d'identité énergétique — composant principal du module Travaux d'optimisation.
 *
 * Point d'entrée : remplace l'ancien placeholder `EnergeticSituationPanel`.
 * Consomme `GET /prospects/{id}/energy-card` (bundle unifié : site principal,
 * carte, readings, equipment, operat, actions, buildings, historique).
 *
 * Permissions (décision C10 = A) :
 *   - Écriture : admin_full + admin + auditeur du dossier
 *   - Lecture   : autres rôles ayant déjà accès au dossier
 * Le backend renvoie `can_write` — le front s'aligne dessus (formulaires masqués sinon).
 */
import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
    Gauge, Sparkles, CheckCircle2, AlertTriangle, XCircle, Info, RefreshCw,
    Plus, Trash2, FileText, ThermometerSun, BarChart3, ShieldCheck, Zap, TrendingUp,
    Mail, Send,
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { toast } from 'sonner';
import api from '@/lib/api';
import { cn } from '@/lib/utils';
import EnergyOperatAttestationsCard from './EnergyOperatAttestationsCard';
import EnergySourcesCard from './EnergySourcesCard';
import EnergyMultiSiteView from './EnergyMultiSiteView';
import EnergyPdfPreviewDialog from './EnergyPdfPreviewDialog';
import KnowledgeTooltip from './KnowledgeTooltip';

// ══════════════════════════════════════════════════════════════════════════
// Constantes d'affichage
// ══════════════════════════════════════════════════════════════════════════

const OVERALL_STYLE = {
    maitrisee:         { label: 'Situation maîtrisée',      color: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
    surveillance:      { label: 'Surveillance',             color: 'bg-blue-100 text-blue-800 border-blue-200' },
    action_recommandee:{ label: 'Action recommandée',       color: 'bg-amber-100 text-amber-800 border-amber-300' },
    prioritaire:       { label: 'Prioritaire',              color: 'bg-orange-100 text-orange-800 border-orange-300' },
    regularisation:    { label: 'Régularisation nécessaire',color: 'bg-rose-100 text-rose-800 border-rose-300' },
    insuffisant:       { label: 'Données insuffisantes',    color: 'bg-slate-100 text-slate-700 border-slate-300' },
};

const RELIABILITY_STYLE = {
    verifie:    { label: 'Vérifié',      cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
    declaratif: { label: 'Déclaratif',   cls: 'bg-blue-100 text-blue-700 border-blue-200' },
    estime:     { label: 'Estimé',       cls: 'bg-amber-100 text-amber-800 border-amber-200' },
    a_verifier: { label: 'À vérifier',   cls: 'bg-orange-100 text-orange-800 border-orange-200' },
    inconnu:    { label: 'Inconnu',      cls: 'bg-slate-100 text-slate-700 border-slate-200' },
};

function ReliabilityBadge({ level }) {
    const s = RELIABILITY_STYLE[level] || RELIABILITY_STYLE.inconnu;
    return <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border', s.cls)}>{s.label}</span>;
}

function statusIcon(v) {
    if (['conforme','objectif_atteint','trajectoire_favorable','non_assujetti','exemption_documentee','maitrisee'].includes(v)) return <CheckCircle2 className="w-4 h-4 text-emerald-600" />;
    if (['echeance_depassee','non_conforme','a_risque','regularisation'].includes(v))                                            return <XCircle    className="w-4 h-4 text-rose-600" />;
    if (['a_surveiller','assujetti_a_venir','a_verifier','partiel','a_qualifier','action_recommandee','prioritaire'].includes(v))return <AlertTriangle className="w-4 h-4 text-amber-600" />;
    return <Info className="w-4 h-4 text-slate-400" />;
}

// ══════════════════════════════════════════════════════════════════════════
// Jauge de trajectoire — SVG maison, lisible + responsive
// ══════════════════════════════════════════════════════════════════════════

function TrajectoryGauge({ reduction, projection, obj2030 = 40, obj2040 = 50, obj2050 = 60 }) {
    const clamp = (v) => Math.max(0, Math.min(100, v ?? 0));
    const curr = clamp(reduction);
    const proj = projection != null ? clamp(projection) : null;
    return (
        <div className="w-full">
            <div className="relative h-16 rounded-full bg-gradient-to-r from-slate-100 to-slate-200 overflow-hidden border border-slate-200">
                {/* Zones-objectifs */}
                <div className="absolute inset-y-0" style={{ left: 0, width: `${obj2030}%` }}>
                    <div className="h-full bg-gradient-to-r from-purple-500/70 to-purple-400/40" />
                </div>
                {/* Marker actuel */}
                <div className="absolute inset-y-0 flex items-center" style={{ left: `${curr}%` }} data-testid="trajectory-gauge-current">
                    <div className="w-1 h-full bg-slate-900" />
                    <span className="ml-1 px-1.5 py-0.5 text-[11px] font-bold rounded bg-slate-900 text-white whitespace-nowrap">
                        {reduction != null ? `${reduction.toFixed(1)}%` : '—'}
                    </span>
                </div>
                {/* Marker projection */}
                {proj !== null && (
                    <div className="absolute inset-y-0 flex items-center" style={{ left: `${proj}%` }}>
                        <div className="w-0.5 h-full bg-amber-500 opacity-70" />
                        <span className="ml-1 px-1 py-0.5 text-[9px] rounded bg-amber-500 text-white">proj</span>
                    </div>
                )}
            </div>
            <div className="relative mt-1 h-4 text-[10px] text-slate-500">
                <span className="absolute" style={{ left: '0%' }}>0%</span>
                <span className="absolute -translate-x-1/2" style={{ left: `${obj2030}%` }}>2030 · -{obj2030}%</span>
                <span className="absolute -translate-x-1/2" style={{ left: `${obj2040}%` }}>2040 · -{obj2040}%</span>
                <span className="absolute -translate-x-1/2" style={{ left: `${obj2050}%` }}>2050 · -{obj2050}%</span>
            </div>
        </div>
    );
}

// ══════════════════════════════════════════════════════════════════════════
// Formulaires : sites (édition), reading, equipment, operat
// ══════════════════════════════════════════════════════════════════════════

function SiteEditDialog({ open, onOpenChange, site, onSaved }) {
    const [form, setForm] = useState(site || {});
    useEffect(() => { setForm(site || {}); }, [site]);
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value === '' ? null : e.target.value }));
    const setNum = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value === '' ? null : Number(e.target.value) }));
    const submit = async () => {
        try {
            const payload = {
                label: form.label, address: form.address, postal_code: form.postal_code, city: form.city,
                surface_totale_m2: form.surface_totale_m2, surface_tertiaire_m2: form.surface_tertiaire_m2,
                activity_dominant: form.activity_dominant, proprietaire_ou_locataire: form.proprietaire_ou_locataire,
                buildings_count: form.buildings_count ? Number(form.buildings_count) : null,
            };
            const { data } = await api.patch(`/energy-sites/${site.id}`, payload);
            toast.success('Site enregistré');
            onSaved?.(data);
            onOpenChange(false);
        } catch (e) { toast.error(e.response?.data?.detail || 'Erreur enregistrement'); }
    };
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg" data-testid="energy-site-edit-dialog">
                <DialogHeader><DialogTitle>Éditer le site</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2"><Label>Libellé</Label><Input value={form.label || ''} onChange={set('label')} data-testid="site-label"/></div>
                    <div><Label>Adresse</Label><Input value={form.address || ''} onChange={set('address')} /></div>
                    <div><Label>Ville</Label><Input value={form.city || ''} onChange={set('city')} /></div>
                    <div><Label>Surface totale (m²)</Label><Input type="number" value={form.surface_totale_m2 ?? ''} onChange={setNum('surface_totale_m2')} data-testid="site-surface-totale" /></div>
                    <div><Label>Surface tertiaire (m²)</Label><Input type="number" value={form.surface_tertiaire_m2 ?? ''} onChange={setNum('surface_tertiaire_m2')} data-testid="site-surface-tertiaire"/></div>
                    <div className="col-span-2"><Label>Activité dominante</Label><Input value={form.activity_dominant || ''} onChange={set('activity_dominant')} placeholder="ex : commerce alimentaire, hôtellerie…" /></div>
                    <div className="col-span-2">
                        <Label>Statut propriété</Label>
                        <Select value={form.proprietaire_ou_locataire || ''} onValueChange={(v) => setForm((f)=>({...f, proprietaire_ou_locataire: v}))}>
                            <SelectTrigger data-testid="site-proploc"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="proprietaire">Propriétaire</SelectItem>
                                <SelectItem value="locataire">Locataire</SelectItem>
                                <SelectItem value="mixte">Mixte</SelectItem>
                                <SelectItem value="inconnu">Je ne sais pas</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
                    <Button onClick={submit} data-testid="site-save">Enregistrer</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function AddReadingDialog({ open, onOpenChange, siteId, onAdded }) {
    const [f, setF] = useState({ year: new Date().getFullYear() - 1, kwh_total: '', source: 'facture', reliability: 'declaratif' });
    const submit = async () => {
        if (!f.year || !f.kwh_total) return toast.error('Année et kWh requis');
        try {
            const { data } = await api.post(`/energy-sites/${siteId}/readings`, {
                year: Number(f.year), kwh_total: Number(f.kwh_total), source: f.source, reliability: f.reliability, consumption_type: 'actual',
            });
            toast.success('Consommation enregistrée');
            onAdded?.(data); onOpenChange(false);
        } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    };
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="add-reading-dialog">
                <DialogHeader><DialogTitle>Nouvelle année de consommation</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                    <div><Label>Année</Label><Input type="number" value={f.year} onChange={(e)=>setF({...f, year:e.target.value})} data-testid="reading-year"/></div>
                    <div><Label>kWh total</Label><Input type="number" value={f.kwh_total} onChange={(e)=>setF({...f, kwh_total:e.target.value})} data-testid="reading-kwh" /></div>
                    <div><Label>Source</Label>
                        <Select value={f.source} onValueChange={(v)=>setF({...f, source:v})}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="operat">OPERAT</SelectItem>
                                <SelectItem value="facture">Facture énergie</SelectItem>
                                <SelectItem value="declaratif">Déclaratif</SelectItem>
                                <SelectItem value="estime">Estimation</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div><Label>Fiabilité</Label>
                        <Select value={f.reliability} onValueChange={(v)=>setF({...f, reliability:v})}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="verifie">Vérifié</SelectItem>
                                <SelectItem value="declaratif">Déclaratif</SelectItem>
                                <SelectItem value="estime">Estimé</SelectItem>
                                <SelectItem value="a_verifier">À vérifier</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                <DialogFooter><Button variant="outline" onClick={()=>onOpenChange(false)}>Annuler</Button><Button onClick={submit} data-testid="reading-save">Enregistrer</Button></DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function AddEquipmentDialog({ open, onOpenChange, siteId, onAdded }) {
    const [f, setF] = useState({ category: 'chauffage', brand: '', power_kw: '', install_year: '', condition: '' });
    const submit = async () => {
        try {
            const { data } = await api.post(`/energy-sites/${siteId}/equipment`, {
                category: f.category, brand: f.brand || null, power_kw: f.power_kw ? Number(f.power_kw) : null,
                install_year: f.install_year ? Number(f.install_year) : null, condition: f.condition || null, reliability: 'declaratif',
            });
            toast.success('Équipement ajouté'); onAdded?.(data); onOpenChange(false);
        } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    };
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="add-equipment-dialog">
                <DialogHeader><DialogTitle>Ajouter un équipement</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                    <div><Label>Catégorie</Label>
                        <Select value={f.category} onValueChange={(v)=>setF({...f, category:v})}>
                            <SelectTrigger data-testid="equipment-cat"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {['chauffage','climatisation','ventilation','froid','ecs','eclairage','gtb','autres'].map((c)=><SelectItem key={c} value={c}>{c}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div><Label>Marque / modèle</Label><Input value={f.brand} onChange={(e)=>setF({...f, brand:e.target.value})} /></div>
                    <div><Label>Puissance (kW)</Label><Input type="number" value={f.power_kw} onChange={(e)=>setF({...f, power_kw:e.target.value})} data-testid="equipment-kw" /></div>
                    <div><Label>Année d&apos;installation</Label><Input type="number" value={f.install_year} onChange={(e)=>setF({...f, install_year:e.target.value})} /></div>
                    <div className="col-span-2"><Label>État</Label>
                        <Select value={f.condition} onValueChange={(v)=>setF({...f, condition:v})}>
                            <SelectTrigger><SelectValue placeholder="…" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="bon">Bon</SelectItem>
                                <SelectItem value="moyen">Moyen</SelectItem>
                                <SelectItem value="vetuste">Vétuste</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                <DialogFooter><Button variant="outline" onClick={()=>onOpenChange(false)}>Annuler</Button><Button onClick={submit} data-testid="equipment-save">Ajouter</Button></DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function AddOperatDialog({ open, onOpenChange, siteId, onAdded }) {
    const [f, setF] = useState({ annee_reference: '', cref: '', cabs: '', derniere_annee_declaree: '', derniere_conso_declaree: '' });
    const submit = async () => {
        try {
            const { data } = await api.post(`/energy-sites/${siteId}/operat`, {
                efa_id: null, categorie: null,
                annee_reference: f.annee_reference ? Number(f.annee_reference) : null,
                cref: f.cref ? Number(f.cref) : null,
                cabs: f.cabs ? Number(f.cabs) : null,
                derniere_annee_declaree: f.derniere_annee_declaree ? Number(f.derniere_annee_declaree) : null,
                derniere_conso_declaree: f.derniere_conso_declaree ? Number(f.derniere_conso_declaree) : null,
            });
            toast.success('Déclaration OPERAT enregistrée'); onAdded?.(data); onOpenChange(false);
        } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    };
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="add-operat-dialog">
                <DialogHeader><DialogTitle>Déclaration OPERAT</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                    <div><Label>Année de référence (2010-2022)</Label><Input type="number" value={f.annee_reference} onChange={(e)=>setF({...f, annee_reference:e.target.value})} data-testid="operat-year"/></div>
                    <div><Label>Cref (kWh)</Label><Input type="number" value={f.cref} onChange={(e)=>setF({...f, cref:e.target.value})} data-testid="operat-cref"/></div>
                    <div><Label>Cabs 2030 (kWh)</Label><Input type="number" value={f.cabs} onChange={(e)=>setF({...f, cabs:e.target.value})} /></div>
                    <div><Label>Dernière année déclarée</Label><Input type="number" value={f.derniere_annee_declaree} onChange={(e)=>setF({...f, derniere_annee_declaree:e.target.value})} /></div>
                    <div className="col-span-2"><Label>Dernière conso déclarée (kWh)</Label><Input type="number" value={f.derniere_conso_declaree} onChange={(e)=>setF({...f, derniere_conso_declaree:e.target.value})} data-testid="operat-conso"/></div>
                </div>
                <DialogFooter><Button variant="outline" onClick={()=>onOpenChange(false)}>Annuler</Button><Button onClick={submit} data-testid="operat-save">Enregistrer</Button></DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ══════════════════════════════════════════════════════════════════════════
// Composant principal
// ══════════════════════════════════════════════════════════════════════════

export default function EnergyCard({ prospect }) {
    const [bundle, setBundle] = useState(null);
    const [loading, setLoading] = useState(true);
    const [recomputing, setRecomputing] = useState(false);
    const [editSite, setEditSite] = useState(false);
    const [addReading, setAddReading] = useState(false);
    const [addEquip, setAddEquip] = useState(false);
    const [addOperat, setAddOperat] = useState(false);

    const load = async () => {
        try {
            setLoading(true);
            const { data } = await api.get(`/prospects/${prospect.id}/energy-card`);
            setBundle(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur chargement Carte');
        } finally { setLoading(false); }
    };
    useEffect(() => { if (prospect?.id) load(); }, [prospect?.id]);

    const recompute = async () => {
        setRecomputing(true);
        try {
            await api.post(`/prospects/${prospect.id}/energy-card/recompute`);
            toast.success('Carte recalculée');
            await load();
        } catch (e) { toast.error(e.response?.data?.detail || 'Erreur recompute'); }
        finally { setRecomputing(false); }
    };

    const deleteItem = async (kind, id) => {
        if (!window.confirm('Supprimer ?')) return;
        try {
            const map = { reading: 'energy-readings', equipment: 'energy-equipment', operat: 'energy-operat' };
            await api.delete(`/${map[kind]}/${id}`);
            toast.success('Supprimé'); await load();
        } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    };

    // ─── PDF (Étape 6) ───────────────────────────────────────────
    const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
    const [snapshots, setSnapshots] = useState([]);
    const [emailSnapshotTarget, setEmailSnapshotTarget] = useState(null);
    const loadSnapshots = async () => {
        try {
            const { data } = await api.get(`/prospects/${prospect.id}/energy-card/snapshots`);
            setSnapshots(data?.snapshots || []);
        } catch (e) { /* silencieux */ }
    };
    useEffect(() => { if (prospect?.id) loadSnapshots(); }, [prospect?.id]);

    const openPdfPreview = () => setPdfPreviewOpen(true);

    const downloadSnapshot = async (snapshotId) => {
        try {
            const res = await api.get(`/card-snapshots/${snapshotId}/download`, { responseType: 'blob' });
            const blob = new Blob([res.data], { type: 'application/pdf' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Carte_snapshot_${snapshotId.slice(0, 8)}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur téléchargement');
        }
    };

    if (loading) {
        return <Card data-testid="energy-card-loading"><CardContent className="p-6 text-slate-500">Chargement de la Carte…</CardContent></Card>;
    }
    if (!bundle) return null;
    const { can_write, principal_site, card, readings, equipment, operat_declarations } = bundle;

    if (!principal_site) {
        return (
            <Card className="border-dashed border-2 border-emerald-300 bg-emerald-50/40" data-testid="energy-card-empty">
                <CardContent className="p-6 text-slate-600">
                    Aucun site déclaré pour ce dossier. {can_write ? 'Ouvrez la Carte à nouveau — un site principal sera créé automatiquement.' : 'Un administrateur doit initialiser ce dossier.'}
                </CardContent>
            </Card>
        );
    }

    const overall = OVERALL_STYLE[card?.overall_status] || OVERALL_STYLE.insuffisant;
    const perf = card?.performance || {};
    const bacs = card?.bacs || {};
    const admin = card?.admin_conformity || {};
    const assuj = card?.assujettissement || {};
    const nba = card?.next_best_action;

    // Historique conso pour le graph
    const chartData = (readings || [])
        .filter(r => r.consumption_type === 'actual')
        .map(r => ({ year: r.year, kwh: r.kwh_total }))
        .sort((a, b) => a.year - b.year);

    return (
        <div className="space-y-4" data-testid="energy-card-root">
            {/* ── EN-TÊTE + Statut global ── */}
            <Card className="border-2 border-emerald-200 bg-gradient-to-br from-emerald-50/60 via-white to-teal-50/40">
                <CardHeader className="pb-3">
                    <div className="flex items-start justify-between flex-wrap gap-3">
                        <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-sm">
                                <Gauge className="w-4 h-4 text-white" />
                            </div>
                            <div>
                                <div>Carte d&apos;identité énergétique</div>
                                <div className="text-xs font-normal text-slate-500 mt-0.5">
                                    Site : <b className="text-slate-700">{principal_site.label}</b>
                                    {principal_site.city ? ` — ${principal_site.city}` : ''}
                                </div>
                            </div>
                        </CardTitle>
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className={cn('inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-bold border', overall.color)} data-testid="energy-overall-status">
                                {statusIcon(card?.overall_status)} {overall.label}
                            </span>
                            {can_write && (
                                <Button size="sm" variant="outline" onClick={recompute} disabled={recomputing} data-testid="energy-recompute">
                                    <RefreshCw className={cn('w-3.5 h-3.5 mr-1', recomputing && 'animate-spin')} /> Recalculer
                                </Button>
                            )}
                        </div>
                    </div>
                </CardHeader>

                <CardContent className="space-y-4">
                    {/* Fiche site (surface, propriétaire) */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                        <div className="p-2 rounded-lg bg-white border border-slate-200">
                            <div className="text-slate-400 flex items-center gap-1">Surface tertiaire <KnowledgeTooltip entryKey="sub" size="xs" /></div>
                            <div className="font-bold text-slate-800">{principal_site.surface_tertiaire_m2 ? `${principal_site.surface_tertiaire_m2} m²` : '—'}</div>
                        </div>
                        <div className="p-2 rounded-lg bg-white border border-slate-200">
                            <div className="text-slate-400">Surface totale</div>
                            <div className="font-bold text-slate-800">{principal_site.surface_totale_m2 ? `${principal_site.surface_totale_m2} m²` : '—'}</div>
                        </div>
                        <div className="p-2 rounded-lg bg-white border border-slate-200">
                            <div className="text-slate-400">Statut propriété</div>
                            <div className="font-bold text-slate-800 capitalize">{principal_site.proprietaire_ou_locataire || '—'}</div>
                        </div>
                        <div className="p-2 rounded-lg bg-white border border-slate-200">
                            <div className="text-slate-400 flex items-center gap-1">Activité <KnowledgeTooltip entryKey="activite_dominante" size="xs" /></div>
                            <div className="font-bold text-slate-800 truncate">{principal_site.activity_dominant || '—'}</div>
                        </div>
                    </div>
                    {can_write && (
                        <div className="flex justify-end">
                            <Button size="sm" variant="ghost" onClick={()=>setEditSite(true)} data-testid="energy-edit-site">Éditer la fiche site</Button>
                        </div>
                    )}

                    {/* Prochaine action recommandée */}
                    {nba && (
                        <div className="p-3 rounded-lg bg-white border border-slate-200 flex items-start gap-2" data-testid="energy-next-action">
                            <Sparkles className="w-4 h-4 text-purple-600 mt-0.5 shrink-0" />
                            <div className="flex-1 min-w-0">
                                <div className="font-bold text-slate-800 text-sm">Prochaine meilleure action</div>
                                <div className="text-sm text-slate-700 mt-0.5">{nba.label}</div>
                                <div className="text-xs text-slate-500 mt-0.5">{nba.reason}</div>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* ── SOURCES DE DONNÉES & COHÉRENCE ── */}
            <EnergySourcesCard prospect={prospect} bundle={bundle} />

            {/* ── VUE SOCIÉTÉ CONSOLIDÉE (visible si >= 2 sites) ── */}
            <EnergyMultiSiteView prospectId={prospect.id} />

            {/* ── TRAJECTOIRE ── */}
            <Card data-testid="energy-trajectory-section">
                <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><TrendingUp className="w-4 h-4 text-purple-600" /> Trajectoire énergétique <KnowledgeTooltip entryKey="crelat" size="sm" /></CardTitle></CardHeader>
                <CardContent>
                    <TrajectoryGauge
                        reduction={perf.reduction_actuelle_pct}
                        projection={perf.projection?.projection_2030_pct}
                        obj2030={perf.objectif_2030_pct || 40}
                        obj2040={perf.objectif_2040_pct || 50}
                        obj2050={perf.objectif_2050_pct || 60}
                    />
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-4 text-xs">
                        <div className="p-2 rounded bg-slate-50 border border-slate-200">
                            <div className="text-slate-400">Réduction actuelle</div>
                            <div className="font-bold text-slate-800">{perf.reduction_actuelle_pct != null ? `${perf.reduction_actuelle_pct}%` : '—'}</div>
                        </div>
                        <div className="p-2 rounded bg-slate-50 border border-slate-200">
                            <div className="text-slate-400">Écart 2030</div>
                            <div className="font-bold text-slate-800">{perf.ecart_2030_points != null ? `${perf.ecart_2030_points} pts` : '—'}</div>
                        </div>
                        <div className="p-2 rounded bg-slate-50 border border-slate-200">
                            <div className="text-slate-400">Cabs atteint</div>
                            <div className="font-bold text-slate-800">{perf.cabs_atteint === true ? 'Oui' : perf.cabs_atteint === false ? 'Non' : '—'}</div>
                        </div>
                        <div className="p-2 rounded bg-slate-50 border border-slate-200">
                            <div className="text-slate-400">Projection 2030</div>
                            <div className="font-bold text-slate-800">
                                {perf.projection?.projection_2030_pct != null ? `${perf.projection.projection_2030_pct}%` : '—'}
                                {perf.projection?.reliability && <span className="ml-1 text-[10px] text-slate-500">({perf.projection.reliability})</span>}
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* ── DOUBLE CARTE : Réglementation + BACS ── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Réglementation EET + OPERAT */}
                <Card data-testid="energy-regulatory">
                    <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-blue-600" /> Réglementation (Décret Tertiaire) <KnowledgeTooltip entryKey="eet" size="sm" /></CardTitle></CardHeader>
                    <CardContent className="space-y-3">
                        <div className="flex items-center justify-between">
                            <span className="text-sm text-slate-600 flex items-center gap-1">Assujettissement <KnowledgeTooltip entryKey="assujettissement" size="xs" /></span>
                            <span className="inline-flex items-center gap-1 text-sm font-bold capitalize">
                                {statusIcon(assuj.status)} {(assuj.status || 'inconnu').replace('_',' ')}
                            </span>
                        </div>
                        {assuj.reasoning?.length > 0 && (
                            <ul className="text-xs text-slate-500 list-disc pl-4 space-y-0.5">
                                {assuj.reasoning.slice(0, 3).map((r, i) => <li key={i}>{r}</li>)}
                            </ul>
                        )}
                        <div className="flex items-center justify-between border-t pt-2">
                            <span className="text-sm text-slate-600 flex items-center gap-1">Conformité OPERAT <KnowledgeTooltip entryKey="operat" size="xs" /></span>
                            <span className="inline-flex items-center gap-1 text-sm font-bold capitalize">
                                {statusIcon(admin.status)} {(admin.status || 'inconnu').replace('_',' ')}
                            </span>
                        </div>
                        {admin.next_deadline && (
                            <div className="text-xs text-slate-500">Prochaine échéance : <b className="text-slate-700">{admin.next_deadline}</b></div>
                        )}
                        {admin.missing?.length > 0 && (
                            <div className="text-xs text-amber-700 bg-amber-50 rounded p-2 border border-amber-200">
                                À compléter : {admin.missing.join(', ')}
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* BACS */}
                <Card data-testid="energy-bacs">
                    <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Zap className="w-4 h-4 text-amber-600" /> Décret BACS</CardTitle></CardHeader>
                    <CardContent className="space-y-3">
                        <div className="flex items-center justify-between">
                            <span className="text-sm text-slate-600">Statut</span>
                            <span className="inline-flex items-center gap-1 text-sm font-bold capitalize">
                                {statusIcon(bacs.status)} {(bacs.status || 'inconnu').replace('_',' ')}
                            </span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                            <div className="p-2 rounded bg-slate-50">
                                <div className="text-slate-400">Puissance CVC</div>
                                <div className="font-bold text-slate-800">{bacs.puissance_cvc_kw != null ? `${bacs.puissance_cvc_kw} kW` : '—'}</div>
                            </div>
                            <div className="p-2 rounded bg-slate-50">
                                <div className="text-slate-400">Seuil applicable</div>
                                <div className="font-bold text-slate-800">{bacs.seuil_applicable_kw ? `${bacs.seuil_applicable_kw} kW` : '—'}</div>
                            </div>
                            <div className="p-2 rounded bg-slate-50">
                                <div className="text-slate-400">Échéance</div>
                                <div className="font-bold text-slate-800">{bacs.echeance || '—'}</div>
                            </div>
                            <div className="p-2 rounded bg-slate-50">
                                <div className="text-slate-400">Jours restants</div>
                                <div className={cn('font-bold', bacs.jours_restants < 0 ? 'text-rose-600' : bacs.jours_restants < 365 ? 'text-amber-600' : 'text-slate-800')}>
                                    {bacs.jours_restants != null ? `${bacs.jours_restants}` : '—'}
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* ── HISTORIQUE CONSO ── */}
            <Card data-testid="energy-readings-section">
                <CardHeader className="pb-2 flex flex-row items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2"><BarChart3 className="w-4 h-4 text-indigo-600" /> Historique consommations</CardTitle>
                    {can_write && <Button size="sm" variant="outline" onClick={()=>setAddReading(true)} data-testid="add-reading-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Ajouter</Button>}
                </CardHeader>
                <CardContent>
                    {chartData.length > 0 ? (
                        <div className="h-52">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={chartData}>
                                    <XAxis dataKey="year" tick={{ fontSize: 11 }} />
                                    <YAxis tick={{ fontSize: 11 }} />
                                    <Tooltip />
                                    {perf.cref && <ReferenceLine y={perf.cref} stroke="#7c3aed" strokeDasharray="4 4" label={{ value:'Cref', fontSize: 10, fill:'#7c3aed'}} />}
                                    {perf.cabs && <ReferenceLine y={perf.cabs} stroke="#f59e0b" strokeDasharray="4 4" label={{ value:'Cabs', fontSize: 10, fill:'#f59e0b'}} />}
                                    <Line type="monotone" dataKey="kwh" stroke="#4f46e5" strokeWidth={2} dot={{ r: 4 }} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    ) : (
                        <p className="text-sm text-slate-500 italic">Aucune consommation renseignée.</p>
                    )}
                    {readings?.length > 0 && (
                        <div className="mt-3 space-y-1">
                            {readings.map((r) => (
                                <div key={r.id} className="flex items-center justify-between text-xs px-2 py-1 rounded hover:bg-slate-50">
                                    <span><b>{r.year}</b> — {r.kwh_total?.toLocaleString()} kWh <span className="text-slate-400">({r.source})</span></span>
                                    <div className="flex items-center gap-2">
                                        <ReliabilityBadge level={r.reliability} />
                                        {can_write && <button onClick={()=>deleteItem('reading', r.id)} className="text-slate-400 hover:text-rose-600"><Trash2 className="w-3 h-3" /></button>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* ── OPERAT ── */}
            <Card data-testid="energy-operat-section">
                <CardHeader className="pb-2 flex flex-row items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2"><FileText className="w-4 h-4 text-purple-600" /> Déclarations OPERAT</CardTitle>
                    {can_write && <Button size="sm" variant="outline" onClick={()=>setAddOperat(true)} data-testid="add-operat-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Ajouter</Button>}
                </CardHeader>
                <CardContent>
                    {operat_declarations?.length > 0 ? (
                        <div className="space-y-2">
                            {operat_declarations.map((o) => (
                                <div key={o.id} className="flex items-center justify-between text-xs p-2 rounded bg-slate-50 border border-slate-200">
                                    <div>
                                        <b>Année de réf {o.annee_reference || '?'}</b> — Cref {o.cref?.toLocaleString() || '?'} · Cabs {o.cabs?.toLocaleString() || '?'}
                                        {o.derniere_annee_declaree && <span className="text-slate-500"> · dernière conso {o.derniere_annee_declaree} : {o.derniere_conso_declaree?.toLocaleString()}</span>}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <ReliabilityBadge level={o.reliability} />
                                        {can_write && <button onClick={()=>deleteItem('operat', o.id)} className="text-slate-400 hover:text-rose-600"><Trash2 className="w-3 h-3" /></button>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-sm text-slate-500 italic">Aucune déclaration OPERAT renseignée.</p>
                    )}
                </CardContent>
            </Card>

            {/* ── PATRIMOINE TECHNIQUE ── */}
            <Card data-testid="energy-equipment-section">
                <CardHeader className="pb-2 flex flex-row items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2"><ThermometerSun className="w-4 h-4 text-orange-600" /> Patrimoine technique <KnowledgeTooltip entryKey="patrimoine_technique" size="sm" /></CardTitle>
                    {can_write && <Button size="sm" variant="outline" onClick={()=>setAddEquip(true)} data-testid="add-equipment-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Ajouter</Button>}
                </CardHeader>
                <CardContent>
                    {equipment?.length > 0 ? (
                        <div className="space-y-1">
                            {equipment.map((e) => (
                                <div key={e.id} className="flex items-center justify-between text-xs p-2 rounded hover:bg-slate-50 border-b last:border-b-0">
                                    <div>
                                        <b className="capitalize">{e.category}</b>
                                        {e.brand && ` · ${e.brand}`}
                                        {e.power_kw != null && ` · ${e.power_kw} kW`}
                                        {e.install_year && ` · ${e.install_year}`}
                                        {e.condition && ` · ${e.condition}`}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <ReliabilityBadge level={e.reliability} />
                                        {can_write && <button onClick={()=>deleteItem('equipment', e.id)} className="text-slate-400 hover:text-rose-600"><Trash2 className="w-3 h-3" /></button>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-sm text-slate-500 italic">Aucun équipement renseigné.</p>
                    )}
                </CardContent>
            </Card>

            {/* ── SCORE DÉTAILLÉ ── */}
            {card?.score_breakdown?.length > 0 && (
                <Card data-testid="energy-score-section">
                    <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Sparkles className="w-4 h-4 text-purple-600" /> Score d&apos;opportunité — {card.opportunity_score}/100</CardTitle></CardHeader>
                    <CardContent>
                        <div className="space-y-2">
                            {card.score_breakdown.map((b, i) => (
                                <div key={i}>
                                    <div className="flex items-center justify-between text-xs mb-0.5">
                                        <span className="text-slate-700 font-medium">{b.axis}</span>
                                        <span className="text-slate-500">{b.points}/{b.max}</span>
                                    </div>
                                    <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                                        <div className="h-full bg-gradient-to-r from-purple-500 to-indigo-500" style={{ width: `${(b.points/b.max)*100}%` }} />
                                    </div>
                                    <div className="text-[10px] text-slate-400 mt-0.5">{b.reason}</div>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* PDF — Génération WeasyPrint Direction B (Étape 6) */}
            <Card className="border-purple-200 bg-gradient-to-br from-purple-50/40 to-indigo-50/40" data-testid="energy-pdf-card">
                <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                        <FileText className="w-4 h-4 text-purple-600" /> Carte d&apos;identité PDF premium
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                    <div className="flex items-start justify-between flex-wrap gap-3">
                        <p className="text-sm text-slate-600 flex-1 min-w-[200px]">
                            {can_write
                                ? <>Génère un PDF client premium (Direction B — moderne éditorial) avec toutes les données de la Carte à cet instant. Un <b>snapshot immuable</b> est enregistré pour audit.</>
                                : <>En lecture seule. Tu peux <b>télécharger</b> ou <b>envoyer au client</b> les PDF déjà émis ci-dessous. Seul un admin_full ou l&apos;auditeur assigné peut générer un nouveau PDF.</>}
                        </p>
                        {can_write && (
                            <Button
                                onClick={openPdfPreview}
                                size="sm"
                                className="bg-purple-600 hover:bg-purple-700 text-white"
                                data-testid="energy-pdf-generate-btn"
                            >
                                <FileText className="w-3.5 h-3.5 mr-1" />
                                Aperçu & envoi
                            </Button>
                        )}
                    </div>
                    {snapshots.length > 0 && (
                        <div className="border-t pt-3 space-y-1.5" data-testid="energy-pdf-snapshots-list">
                            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                Historique des PDF émis ({snapshots.length})
                            </div>
                            {snapshots.slice(0, 5).map((s) => (
                                <div key={s.id} className="flex items-center justify-between text-xs p-2 rounded bg-white border border-slate-200 gap-2 flex-wrap">
                                    <div className="min-w-0 flex-1">
                                        <b>{new Date(s.emitted_at).toLocaleString('fr-FR')}</b>
                                        <span className="text-slate-500"> — {s.emitter_name} · {s.emitter_role || '—'}</span>
                                    </div>
                                    <div className="flex items-center gap-1 shrink-0">
                                        <Button
                                            size="sm" variant="ghost"
                                            onClick={() => downloadSnapshot(s.id)}
                                            data-testid={`snapshot-download-${s.id}`}
                                        >
                                            <FileText className="w-3.5 h-3.5 mr-1" /> Télécharger
                                        </Button>
                                        <Button
                                            size="sm" variant="ghost"
                                            className="text-purple-700 hover:text-purple-800"
                                            onClick={() => setEmailSnapshotTarget(s)}
                                            data-testid={`snapshot-email-${s.id}`}
                                        >
                                            <Mail className="w-3.5 h-3.5 mr-1" /> Envoyer au client
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* ── ATTESTATIONS OPERAT — source documentaire multi-sites ── */}
            <EnergyOperatAttestationsCard
                bundle={bundle}
                prospectId={prospect.id}
                onChanged={load}
            />

            {/* Dialogs */}
            <SiteEditDialog open={editSite} onOpenChange={setEditSite} site={principal_site} onSaved={load} />
            <AddReadingDialog open={addReading} onOpenChange={setAddReading} siteId={principal_site.id} onAdded={load} />
            <AddEquipmentDialog open={addEquip} onOpenChange={setAddEquip} siteId={principal_site.id} onAdded={load} />
            <AddOperatDialog open={addOperat} onOpenChange={setAddOperat} siteId={principal_site.id} onAdded={load} />
            <EnergyPdfPreviewDialog
                open={pdfPreviewOpen}
                onOpenChange={setPdfPreviewOpen}
                prospect={prospect}
                onGenerated={loadSnapshots}
            />
            <SnapshotEmailDialog
                snapshot={emailSnapshotTarget}
                prospect={prospect}
                onClose={() => setEmailSnapshotTarget(null)}
            />
        </div>
    );
}

/**
 * Dialog d'envoi d'un snapshot existant par email — accessible à tous les rôles
 * ayant accès en lecture (VP, apporteurs, etc.). Réutilise le snapshot immuable
 * (pas de nouvelle génération), donc lecture seule côté permissions.
 */
function SnapshotEmailDialog({ snapshot, prospect, onClose }) {
    const [to, setTo] = useState('');
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [sending, setSending] = useState(false);

    useEffect(() => {
        if (snapshot && prospect) {
            setTo(prospect?.email || '');
            setSubject('');
            setMessage('');
        }
    }, [snapshot, prospect]);

    if (!snapshot) return null;

    const send = async () => {
        const emails = to.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);
        if (emails.length === 0 || !emails.every((e) => /@/.test(e))) {
            toast.error('Renseigne au moins un email valide'); return;
        }
        setSending(true);
        try {
            await api.post(`/prospects/${prospect.id}/energy-card/pdf/email`, {
                to: emails,
                subject: subject || undefined,
                message: message || undefined,
                snapshot_id: snapshot.id,
            });
            toast.success('Email envoyé au client');
            onClose?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur envoi email');
        } finally {
            setSending(false);
        }
    };

    return (
        <Dialog open={!!snapshot} onOpenChange={(v) => !v && onClose?.()}>
            <DialogContent className="max-w-lg" data-testid="snapshot-email-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-purple-600" />
                        Envoyer ce PDF au client
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                    <div className="rounded bg-slate-50 border border-slate-200 px-3 py-2 text-xs">
                        <b>PDF émis le {new Date(snapshot.emitted_at).toLocaleString('fr-FR')}</b>
                        <div className="text-slate-500 mt-0.5">Par {snapshot.emitter_name} · Snapshot #{snapshot.id.slice(0,8)}</div>
                    </div>
                    <div>
                        <Label className="text-xs">Destinataires</Label>
                        <Input
                            value={to} onChange={(e) => setTo(e.target.value)}
                            placeholder="client@societe.fr, contact@societe.fr"
                            data-testid="snapshot-email-to"
                        />
                        <p className="text-[10px] text-slate-400 mt-1">Sépare plusieurs emails par une virgule ou un espace.</p>
                    </div>
                    <div>
                        <Label className="text-xs">Objet (optionnel)</Label>
                        <Input
                            value={subject} onChange={(e) => setSubject(e.target.value)}
                            placeholder={`Votre Carte d'identité énergétique — ${prospect?.company_name || ''}`}
                            data-testid="snapshot-email-subject"
                        />
                    </div>
                    <div>
                        <Label className="text-xs">Message personnalisé (optionnel)</Label>
                        <textarea
                            className="w-full min-h-[100px] p-2 rounded border border-slate-200 text-sm"
                            value={message} onChange={(e) => setMessage(e.target.value)}
                            placeholder="Bonjour, veuillez trouver ci-joint votre Carte d'identité énergétique…"
                            data-testid="snapshot-email-message"
                        />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Annuler</Button>
                    <Button
                        onClick={send} disabled={sending}
                        className="bg-purple-600 hover:bg-purple-700 text-white"
                        data-testid="snapshot-email-send-btn"
                    >
                        <Send className={`w-4 h-4 mr-1 ${sending ? 'animate-pulse' : ''}`} />
                        {sending ? 'Envoi…' : 'Envoyer'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
